import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Popover } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import IContact from '@constants/interfaces/contacts';
import { appLinks, contactEditLink } from './constants';
import { ADMIN_PATH, ADMIN_PAGE_TITLE, useAdmin, ADMIN_DATA_CHANGED_EVENT } from 'src/admin/core';
import { WebLink } from './webLink';
import { useNavigate } from 'react-router-dom';
import {
  createCustomerPortalApi,
  deleteAllNotificationsApi,
  deleteNotificationApi,
  deleteNotificationsBulkApi,
  getAccountDeletionStatusApi,
  getAdminCountsApi,
  getMyNotificationsApi,
  logoutApi,
  patchAllNotificationsReadApi,
  patchNotificationReadApi,
  reactivateAccountApi,
} from 'src/services/api';
import { useAddEditFormActions } from './AddEditFormActionsContext';
import {
  IconClose,
  IconCheck,
  IconTrash,
  IconUserPlaceholder,
  IconArrowLeft,
  IconPencil,
  IconShield,
  IconBell,
  IconCheckSolid,
  IconSquareOutline,
  IconSpinner,
  IconCog,
  IconCurrency,
  IconMail,
  IconHelp,
  IconLock,
  IconNotesDocument,
  IconCreditCardAlt,
  IconLogout,
} from 'src/icons';
import { useAppConfig } from './AppConfigContext';
import { getSupportEmail, getNotificationTitlePrefix } from 'src/services/appConfigService';
import { WEB_APP_DATA_CHANGED_EVENT } from './appDataEvents';

const PAGE_TITLES: Record<string, string> = {
  [appLinks.MAIN_PAGE]: 'Contacts',
  [appLinks.DETAILS]: 'Contact Details',
  [appLinks.ADD]: 'Add Contact',
  [appLinks.EDIT]: 'Edit Contact',
  [appLinks.CHANGE_PASSWORD]: 'Change Password',
  [appLinks.PRICING]: 'Pricing',
  [appLinks.SUBSCRIPTION_SUCCESS]: 'Subscription Success',
  [appLinks.REGISTER_SUCCESS]: 'Registration Success',
  [appLinks.TERMS]: 'Terms of Service',
  [appLinks.PRIVACY]: 'Privacy Policy',
  [appLinks.HELP]: 'Help & Docs',
  [ADMIN_PATH]: ADMIN_PAGE_TITLE,
};

export const Header: React.FC<{
  currentUser: string;
  setCurrentUser: React.Dispatch<React.SetStateAction<string>>;
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  contactSelected?: IContact;
}> = ({ currentUser, setCurrentUser, setIsLoggedIn, contactSelected }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, refreshAdminList } = useAdmin();
  const { config } = useAppConfig();
  const supportEmail = getSupportEmail(config);
  const notificationTitlePrefix = getNotificationTitlePrefix(config);
  const [portalLoading, setPortalLoading] = useState(false);
  const pageTitle = useMemo(
    () =>
      PAGE_TITLES[location.pathname] ??
      (location.pathname.startsWith(appLinks.DETAILS + '/') ? PAGE_TITLES[appLinks.DETAILS] : null) ??
      (location.pathname.startsWith(appLinks.EDIT + '/') ? PAGE_TITLES[appLinks.EDIT] : null) ??
      (location.pathname.startsWith(ADMIN_PATH) ? ADMIN_PAGE_TITLE : null),
    [location.pathname]
  );

  const openManageSubscription = async () => {
    const user = JSON.parse(localStorage.getItem('flower_power_notes_user') || '{}');
    const authToken = user?.AuthToken;
    if (!authToken) return;
    setPortalLoading(true);
    try {
      const data = await createCustomerPortalApi(authToken, window.location.origin + appLinks.MAIN_PAGE);
      if (data?.url) window.location.href = data.url;
    } finally {
      setPortalLoading(false);
    }
  };

  const logout = async () => {
    const user = JSON.parse(localStorage.getItem('flower_power_notes_user') || '{}');
    const token = user?.AuthToken;
    if (token) await logoutApi(token).catch(() => {});
    localStorage.clear();
    refreshAdminList();
    setCurrentUser('');
    setIsLoggedIn(false);
    navigate(appLinks.LOGIN);
  };

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [scheduledDeletionAt, setScheduledDeletionAt] = useState<string | null>(null);
  const [cancelDeletionLoading, setCancelDeletionLoading] = useState(false);

  const userFromStorage = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('flower_power_notes_user') || '{}') : {};
  const authToken = userFromStorage?.AuthToken;
  const userId = userFromStorage?.UserId != null ? Number(userFromStorage.UserId) : null;

  const [notifications, setNotifications] = useState<{ id: number; title: string; body: string; read: boolean; createdAt?: string }[]>([]);
  const [notificationActionId, setNotificationActionId] = useState<number | null>(null);
  const [adminNotificationCount, setAdminNotificationCount] = useState(0);

  const loadAdminCounts = useCallback(() => {
    if (!authToken) return;
    getAdminCountsApi(authToken).then((res) => {
      if (!res.error && res.notifications != null) setAdminNotificationCount(res.notifications);
    });
  }, [authToken]);

  const loadNotifications = useCallback(() => {
    if (!authToken) {
      setNotifications([]);
      return;
    }
    getMyNotificationsApi(authToken).then((res) => {
      if (res.notifications) setNotifications(res.notifications);
    });
  }, [authToken]);
  useEffect(() => {
    loadNotifications();
  }, [authToken, loadNotifications]);

  useEffect(() => {
    const handler = () => loadNotifications();
    window.addEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
  }, [loadNotifications]);

  useEffect(() => {
    const handler = () => loadAdminCounts();
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
  }, [loadAdminCounts]);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const handleMarkNotificationRead = async (e: React.MouseEvent, n: { id: number; read: boolean }) => {
    e.preventDefault();
    e.stopPropagation();
    if (n.read || !authToken) return;
    setNotificationActionId(n.id);
    const err = await patchNotificationReadApi(authToken, n.id);
    setNotificationActionId(null);
    if (!err.error) {
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    } else {
      loadNotifications();
    }
  };
  const handleDeleteNotification = async (e: React.MouseEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authToken) return;
    setNotificationActionId(id);
    setNotifications((prev) => prev.filter((x) => x.id !== id));
    const err = await deleteNotificationApi(authToken, id);
    setNotificationActionId(null);
    if (err.error) {
      loadNotifications();
    }
  };
  const [markAllReadLoading, setMarkAllReadLoading] = useState(false);
  const settingsPopoverButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeSettingsPopover = () => settingsPopoverButtonRef.current?.click();
  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authToken || unreadCount === 0) return;
    setMarkAllReadLoading(true);
    const err = await patchAllNotificationsReadApi(authToken);
    setMarkAllReadLoading(false);
    if (!err.error) {
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    } else {
      loadNotifications();
    }
  };
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteBulkLoading, setDeleteBulkLoading] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(notifications.map((n) => n.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };
  const handleDeleteSelected = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authToken || selectedIds.size === 0) return;
    setDeleteBulkLoading(true);
    const ids = Array.from(selectedIds);
    setNotifications((prev) => prev.filter((x) => !selectedIds.has(x.id)));
    setSelectedIds(new Set());
    setSelectionMode(false);
    const err = await deleteNotificationsBulkApi(authToken, ids);
    setDeleteBulkLoading(false);
    if (err.error) loadNotifications();
  };
  const handleDeleteAll = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authToken || notifications.length === 0) return;
    if (!window.confirm('Delete all notifications? This cannot be undone.')) return;
    setDeleteAllLoading(true);
    setNotifications([]);
    setSelectedIds(new Set());
    setSelectionMode(false);
    const err = await deleteAllNotificationsApi(authToken);
    setDeleteAllLoading(false);
    if (err.error) loadNotifications();
  };
  const formatNotificationTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return d.toLocaleDateString();
  };

  const refreshDeletionStatus = useCallback(() => {
    if (!authToken) {
      setScheduledDeletionAt(null);
      return;
    }
    getAccountDeletionStatusApi(authToken).then((res) => {
      if (res.scheduledDeletionAt) {
        setScheduledDeletionAt(res.scheduledDeletionAt);
      } else {
        setScheduledDeletionAt(null);
      }
    });
  }, [authToken]);

  useEffect(() => {
    refreshDeletionStatus();
  }, [authToken, location.pathname, refreshDeletionStatus]);

  useEffect(() => {
    const handler = () => refreshDeletionStatus();
    window.addEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(WEB_APP_DATA_CHANGED_EVENT, handler);
  }, [refreshDeletionStatus]);

  const handleCancelDeletion = async () => {
    if (!authToken || !userId) return;
    setCancelDeletionLoading(true);
    try {
      const result = await reactivateAccountApi(authToken, userId);
      if (result.error) {
        setDeleteError(result.error);
        return;
      }
      setScheduledDeletionAt(null);
      setDeleteError(null);
    } catch {
      setDeleteError('Failed to cancel deletion.');
    } finally {
      setCancelDeletionLoading(false);
    }
  };

  const isContactDetailsPage = location.pathname.startsWith(appLinks.DETAILS + '/');
  const isAddContactPage = location.pathname === appLinks.ADD;
  const isEditContactPage = location.pathname.startsWith(appLinks.EDIT + '/');
  const isAddOrEditPage = isAddContactPage || isEditContactPage;
  const isAdminPage = location.pathname === ADMIN_PATH || location.pathname.startsWith(ADMIN_PATH + '/');

  useEffect(() => {
    if (isAdmin && authToken) loadAdminCounts();
  }, [isAdmin, authToken, loadAdminCounts]);

  const addEditActions = useAddEditFormActions();
  const addEditFormActions = addEditActions?.actions ?? null;

  const settingsMenuClass =
    'flex items-center gap-3 w-full px-4 py-3 rounded-xl text-left text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200';
  const settingsIconClass = 'w-5 h-5 shrink-0 text-gray-500';

  const deletionDate = scheduledDeletionAt
    ? new Date(scheduledDeletionAt).toLocaleDateString(undefined, { dateStyle: 'long' })
    : '';

  return (
    <>
      {scheduledDeletionAt ? (
        <div className="top-0 right-0 left-0 z-[9999] fixed bg-amber-100/90 shadow-md backdrop-blur-md px-4 py-1 w-full font-medium text-amber-900 text-sm text-center">
          <span>Your account will be permanently deleted on {deletionDate}. </span>
          <button
            type="button"
            onClick={handleCancelDeletion}
            disabled={cancelDeletionLoading}
            className="disabled:opacity-50 ml-1 font-semibold underline hover:no-underline"
          >
            {cancelDeletionLoading ? 'Cancelling…' : 'Cancel deletion'}
          </button>
        </div>
      ) : null}
      <header
        className={`fixed left-0 right-0 top-0 z-40 w-full transition-all duration-300 ${
          isContactDetailsPage || isAddOrEditPage || isAdminPage
            ? 'bg-gradient-to-br from-sky-800 via-sky-700 to-sky-600 py-5 shadow-lg shadow-sky-900/20 ring-1 ring-white/10'
            : 'bg-gradient-to-r from-sky-700 to-sky-600 py-5 shadow-md shadow-sky-900/10 ring-1 ring-white/5'
        }`}
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1000px]">
        <nav className="z-50 relative flex justify-between items-center">
          <div className="flex items-center gap-x-4 md:gap-x-8 min-w-0">
            {isContactDetailsPage && contactSelected ? (
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex justify-center items-center bg-white/10 border-2 border-white/20 rounded-full w-10 h-10 overflow-hidden shrink-0">
                  {contactSelected.PhotoUrl ? (
                    <img
                      src={contactSelected.PhotoUrl}
                      alt={contactSelected.Name || 'Profile'}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <IconUserPlaceholder className="w-5 h-5 text-white/70" />
                  )}
                </div>
                <span className="drop-shadow-sm font-bold text-white text-xl sm:text-2xl truncate">
                  {contactSelected.Name || 'No name'}
                </span>
              </div>
            ) : (
              <>
                {(!isContactDetailsPage || !contactSelected) && (
                  <span className="inline-flex">
                    <WebLink to={appLinks.MAIN_PAGE}>
                      <img
                        src={`${process.env.PUBLIC_URL || ''}/icons/notes-icon-inverted.png`}
                        alt="icon"
                        width={isAddOrEditPage ? 32 : 40}
                        height={isAddOrEditPage ? 32 : 40}
                        className="block shrink-0"
                      />
                    </WebLink>
                  </span>
                )}
                {isContactDetailsPage && !contactSelected && (
                  <h1 className="drop-shadow-sm font-bold text-white text-xl sm:text-2xl md:text-3xl truncate tracking-tight">
                    Contact Details
                  </h1>
                )}
                {!isContactDetailsPage && !isAddOrEditPage && pageTitle && !isAdminPage && (
                  <h1 className="drop-shadow-sm font-bold text-white text-xl sm:text-2xl md:text-3xl truncate tracking-tight">
                    {pageTitle}
                  </h1>
                )}
                {isAdminPage && (
                  <h1 className="drop-shadow-sm font-bold text-white text-xl sm:text-2xl md:text-3xl truncate tracking-tight">
                    {ADMIN_PAGE_TITLE}
                  </h1>
                )}
                {isAddOrEditPage && (
                  <h1 className="drop-shadow-sm font-bold text-white text-xl sm:text-2xl md:text-3xl truncate tracking-tight">
                    {isEditContactPage ? 'Edit Contact' : 'Add Contact'}
                  </h1>
                )}
              </>
            )}
          </div>

          {isAddOrEditPage ? (
            <>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 bg-white/50 hover:bg-white/70 shadow-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ring-1 ring-white/60 font-semibold text-sky-800 text-sm active:scale-[0.98] transition-all duration-200"
                >
                  <IconClose className="w-4 h-4 text-sky-800" />
                  <span className="hidden sm:inline">Cancel</span>
                </button>
                {addEditFormActions?.isEditContact && (
                  <button
                    type="button"
                    onClick={addEditFormActions.openDeleteModal}
                    className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 shadow-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ring-1 ring-red-400/50 font-semibold text-white text-sm active:scale-[0.98] transition-all duration-200"
                  >
                    <IconTrash className="w-4 h-4 text-white" />
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                )}
                <button
                  type="submit"
                  form="add-edit-contact-form"
                  className="inline-flex items-center gap-2 bg-white hover:bg-sky-50 shadow-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ring-1 ring-sky-200 font-semibold text-sky-700 text-sm active:scale-[0.98] transition-all duration-200"
                >
                  <IconCheck className="w-4 h-4 text-sky-700" />
                  <span>{isEditContactPage ? 'Update' : 'Save'}</span>
                </button>
              </div>
            </>
          ) : isContactDetailsPage ? (
            <div className="flex items-center gap-2 sm:gap-3">
                <Link
                  to={appLinks.MAIN_PAGE}
                  className="inline-flex items-center gap-2 bg-white/50 hover:bg-white/70 shadow-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ring-1 ring-white/60 font-semibold text-sky-800 text-sm active:scale-[0.98] transition-all duration-200"
                >
                  <IconArrowLeft className="w-4 h-4 text-sky-800 shrink-0" />
                  <span className="hidden sm:inline">Go back</span>
                </Link>
                <Link
                  to={contactSelected?.Id ? contactEditLink(contactSelected.Id) : appLinks.MAIN_PAGE}
                  className="inline-flex items-center gap-2 bg-white hover:bg-sky-50 shadow-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ring-1 ring-sky-200 font-semibold text-sky-700 text-sm active:scale-[0.98] transition-all duration-200"
                >
                  <IconPencil className="w-4 h-4 text-sky-700 shrink-0" />
                  Edit
                </Link>
            </div>
          ) : isAdminPage ? (
            <Link
              to={appLinks.MAIN_PAGE}
              className="inline-flex items-center gap-2 bg-white/50 hover:bg-white/70 shadow-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl ring-1 ring-white/60 font-semibold text-sky-800 text-sm active:scale-[0.98] transition-all duration-200"
            >
              <IconArrowLeft className="w-4 h-4 text-sky-800 shrink-0" />
              <span className="hidden sm:inline">Back to Contacts</span>
            </Link>
          ) : (
            <div className="flex items-center gap-x-3 sm:gap-x-4">
              <span className="hidden sm:block max-w-[160px] md:max-w-[200px] font-medium text-white text-sm truncate">
                {currentUser}
              </span>
              {isAdmin && (
                <Link
                  to={ADMIN_PATH}
                  className="inline-flex relative items-center gap-2 bg-white/15 hover:bg-white/25 px-3 py-2 rounded-xl ring-1 ring-white/20 hover:ring-white/30 font-medium text-white text-sm transition-all duration-200"
                >
                  <IconShield className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline">{ADMIN_PAGE_TITLE}</span>
                  {!isAdminPage && adminNotificationCount > 0 && (
                    <span
                      className="-top-1 -right-1 absolute flex justify-center items-center bg-red-500 px-1 rounded-full min-w-[18px] h-[18px] font-bold text-white text-xs"
                      aria-label={`${adminNotificationCount} admin notifications`}
                    >
                      {adminNotificationCount > 99 ? '99+' : adminNotificationCount}
                    </span>
                  )}
                </Link>
              )}
              <Popover className="relative">
                <Popover.Button
                  onClick={() => loadNotifications()}
                  className="inline-flex justify-center items-center bg-white/15 hover:bg-white/25 rounded-xl focus:outline-none ring-1 ring-white/20 hover:ring-white/30 focus:ring-2 focus:ring-white/50 w-10 h-10 text-white transition-all duration-200"
                  aria-label="Notifications"
                >
                  <IconBell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="top-1 right-1 absolute flex justify-center items-center bg-red-500 px-1 rounded-full min-w-[18px] h-[18px] font-semibold text-white text-xs">
                      {unreadCount}
                    </span>
                  )}
                </Popover.Button>
                <Popover.Panel className="right-0 z-50 absolute bg-white shadow-xl mt-2 border border-gray-100 rounded-2xl ring-1 ring-gray-200 w-80 sm:w-96 max-h-[420px] overflow-hidden origin-top-right">
                  <div className="p-3 border-gray-100 border-b">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 text-sm">Notifications</span>
                        {unreadCount > 0 && (
                          <span className="bg-sky-500 px-2 py-0.5 rounded-full font-medium text-white text-xs">
                            {unreadCount} new
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {unreadCount > 0 && (
                          <button
                            type="button"
                            onClick={handleMarkAllRead}
                            disabled={markAllReadLoading}
                            className="disabled:opacity-50 font-medium text-sky-600 hover:text-sky-700 text-xs hover:underline disabled:no-underline"
                          >
                            {markAllReadLoading ? 'Marking…' : 'Mark all read'}
                          </button>
                        )}
                        <Popover.Button
                          as="button"
                          type="button"
                          className="flex justify-center items-center hover:bg-gray-100 rounded-lg w-8 h-8 text-gray-500 hover:text-gray-700 transition-colors"
                          aria-label="Close notifications"
                        >
                          <IconClose className="w-4 h-4" />
                        </Popover.Button>
                      </div>
                    </div>
                    {notifications.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-gray-100 border-t">
                        {selectionMode ? (
                          <>
                            <button
                              type="button"
                              onClick={selectAll}
                              className="font-medium text-gray-600 hover:text-gray-800 text-xs"
                            >
                              Select all
                            </button>
                            {selectedIds.size > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={clearSelection}
                                  className="font-medium text-gray-600 hover:text-gray-800 text-xs"
                                >
                                  Clear
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDeleteSelected}
                                  disabled={deleteBulkLoading}
                                  className="disabled:opacity-50 font-medium text-red-600 hover:text-red-700 text-xs"
                                >
                                  {deleteBulkLoading ? 'Deleting…' : `Delete ${selectedIds.size} selected`}
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={exitSelectionMode}
                              className="ml-auto font-medium text-gray-600 hover:text-gray-800 text-xs"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setSelectionMode(true)}
                              className="font-medium text-gray-600 hover:text-gray-800 text-xs"
                            >
                              Select
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteAll}
                              disabled={deleteAllLoading}
                              className="disabled:opacity-50 ml-auto font-medium text-red-600 hover:text-red-700 text-xs"
                            >
                              {deleteAllLoading ? 'Deleting…' : 'Delete all'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="max-h-[360px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="flex flex-col justify-center items-center px-4 py-10 text-center">
                        <div className="flex justify-center items-center bg-gray-100 mb-3 rounded-full w-12 h-12 text-gray-400">
                          <IconBell className="w-6 h-6" />
                        </div>
                        <p className="font-medium text-gray-500 text-sm">No notifications</p>
                        <p className="mt-0.5 text-gray-400 text-xs">You're all caught up</p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {notifications.map((n) => {
                          const isActioning = notificationActionId === n.id;
                          return (
                            <li
                              key={n.id}
                              className={`group flex gap-0 transition-colors hover:bg-gray-50/80 ${
                                n.read ? 'bg-white' : 'bg-sky-50/60'
                              } ${isActioning ? 'opacity-70' : ''} ${selectionMode && selectedIds.has(n.id) ? 'ring-1 ring-sky-200 bg-sky-50/40' : ''}`}
                            >
                              {selectionMode && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSelect(n.id);
                                  }}
                                  className="flex justify-center items-center w-8 h-full min-h-[60px] text-gray-400 hover:text-sky-600 shrink-0"
                                  aria-label={selectedIds.has(n.id) ? 'Deselect' : 'Select'}
                                >
                                  {selectedIds.has(n.id) ? (
                                    <IconCheckSolid className="w-5 h-5 text-sky-600" />
                                  ) : (
                                    <IconSquareOutline className="w-5 h-5" />
                                  )}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => handleMarkNotificationRead(e, n)}
                                disabled={isActioning}
                                className={`flex-1 disabled:opacity-90 py-3 min-w-0 text-left ${selectionMode ? 'px-2' : 'px-3'}`}
                              >
                                <div className="flex items-start gap-2">
                                  {!n.read && (
                                    <span className="bg-sky-500 mt-1.5 rounded-full w-2 h-2 shrink-0" aria-hidden title="Unread" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-medium text-sm ${n.read ? 'text-gray-700' : 'text-gray-900'}`}>
                                      {notificationTitlePrefix ? `${notificationTitlePrefix} – ${n.title}` : n.title}
                                    </p>
                                    <p className="mt-0.5 text-gray-600 text-xs line-clamp-2">{n.body}</p>
                                    <p className="mt-1.5 text-gray-400 text-xs">{formatNotificationTime(n.createdAt)}</p>
                                  </div>
                                </div>
                              </button>
                              <div className="flex items-center pr-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={(e) => handleDeleteNotification(e, n.id)}
                                  disabled={isActioning}
                                  className="flex justify-center items-center hover:bg-red-50 disabled:opacity-50 rounded-lg w-8 h-8 text-gray-400 hover:text-red-600 transition-colors"
                                  aria-label="Delete notification"
                                  title="Remove"
                                >
                                  {isActioning ? (
                                    <IconSpinner className="w-4 h-4 text-sky-500 animate-spin" aria-hidden />
                                  ) : (
                                    <IconTrash className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </Popover.Panel>
              </Popover>
              <Popover className="relative">
                <Popover.Button
                  ref={settingsPopoverButtonRef}
                  className="inline-flex justify-center items-center bg-white/15 hover:bg-white/25 rounded-xl focus:outline-none ring-1 ring-white/20 hover:ring-white/30 focus:ring-2 focus:ring-white/50 w-10 h-10 text-white transition-all duration-200"
                  aria-label="Settings"
                >
                  <IconCog className="w-5 h-5" />
                </Popover.Button>
                <Popover.Panel className="right-0 z-50 absolute bg-white shadow-xl mt-2 border border-gray-100 rounded-2xl ring-1 ring-gray-200 w-96 overflow-visible origin-top-right">
                  <div className="p-2">
                    <div className="mb-2 px-3 py-2 border-gray-100 border-b">
                      <p className="font-semibold text-gray-500 text-xs uppercase tracking-wider">Settings</p>
                      <p className="mt-0.5 text-gray-400 text-sm truncate">{currentUser}</p>
                    </div>
                    <Link
                      to={appLinks.ACCOUNT}
                      className={settingsMenuClass}
                      onClick={closeSettingsPopover}
                    >
                      <IconCog className={settingsIconClass} />
                      Account
                    </Link>
                    <Link to={appLinks.PRICING} className={settingsMenuClass} onClick={closeSettingsPopover}>
                      <IconCurrency className={settingsIconClass} />
                      Pricing & subscription
                    </Link>
                    {supportEmail ? (
                      <a href={`mailto:${supportEmail}`} className={settingsMenuClass} onClick={closeSettingsPopover}>
                        <IconMail className={settingsIconClass} />
                        Contact support
                      </a>
                    ) : null}
                    <Link to={appLinks.HELP} className={settingsMenuClass} onClick={closeSettingsPopover}>
                      <IconHelp className={settingsIconClass} />
                      Help & docs
                    </Link>
                    <Link to={appLinks.TERMS} className={settingsMenuClass} onClick={closeSettingsPopover}>
                      <IconNotesDocument className={settingsIconClass} />
                      Terms of Service
                    </Link>
                    <Link to={appLinks.PRIVACY} className={settingsMenuClass} onClick={closeSettingsPopover}>
                      <IconLock className={settingsIconClass} />
                      Privacy Policy
                    </Link>
                    <button
                      type="button"
                      onClick={() => { closeSettingsPopover(); openManageSubscription(); }}
                      disabled={portalLoading}
                      className={`${settingsMenuClass} w-full disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <IconCreditCardAlt className={settingsIconClass} />
                      {portalLoading ? 'Opening...' : 'Manage subscription'}
                    </button>
                    <div className="my-1 border-gray-100 border-t" />
                    <button
                      type="button"
                      onClick={() => { closeSettingsPopover(); logout(); }}
                      className="flex items-center gap-3 hover:bg-red-50 px-4 py-3 rounded-xl w-full font-medium text-red-600 text-sm text-left transition-all duration-200"
                    >
                      <IconLogout className="w-5 h-5 text-red-500 shrink-0" />
                      Log out
                    </button>
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          )}
        </nav>
      </div>
    </header>
    </>
  );
};
